import { useCallback, useEffect, useState } from 'react';
import { navigate } from '../app/router.ts';
import { useSpotifyClient } from '../spotify/useSpotifyClient.ts';
import { searchCatalog, SEARCH_PAGE_SIZE, type SearchHit } from '../spotify/search.ts';
import { getCurrentUser } from '../spotify/api.ts';
import {
  addItem,
  createGroup,
  deleteGroup,
  deleteItem,
  findItemGroup,
  listItems,
  listGroups,
  moveItem,
  saveGroup,
  setGroupImageAuto,
  setGroupImageManual,
} from '../data/catalog.ts';
import { resetAllProgress } from '../data/progress.ts';
import { loadSettings, saveSettings } from '../data/settings.ts';
import { usePlayer } from '../player/PlayerProvider.tsx';
import { DeviceSettings } from './DeviceSettings.tsx';
import type { CatalogGroup, CatalogItem } from '../data/db.ts';
/** Elternbereich: Gruppen anlegen, Folgen suchen und zuordnen. */
export function AdminScreen() {
  const client = useSpotifyClient();
  const { refreshCatalog } = usePlayer();

  const [groups, setGroups] = useState<CatalogGroup[]>([]);
  const [activeGroupId, setActiveGroupId] = useState<string>('');
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [searchOffset, setSearchOffset] = useState(0);
  const [market, setMarket] = useState('DE');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [imagePickerFor, setImagePickerFor] = useState('');

  const reloadGroups = useCallback(async () => {
    const result = await listGroups();
    setGroups(result);
    setActiveGroupId((current) =>
      current && result.some((group) => group.id === current) ? current : (result[0]?.id ?? ''),
    );
  }, []);

  const reloadItems = useCallback(async (groupId: string) => {
    setItems(groupId ? await listItems(groupId) : []);
  }, []);

  useEffect(() => {
    void reloadGroups();
    void getCurrentUser(client).then((user) => {
      if (user?.country) setMarket(user.country);
    });
  }, [client, reloadGroups]);

  useEffect(() => {
    void reloadItems(activeGroupId);
  }, [activeGroupId, reloadItems]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSearch = (offset = 0) =>
    run(async () => {
      const result = await searchCatalog(client, query, market, offset);
      setHits(result);
      setSearchOffset(offset);
      if (result.length === 0) {
        setMessage(offset === 0 ? 'Nichts gefunden.' : 'Keine weiteren Treffer.');
      }
    });

  const handleAddHit = (hit: SearchHit) =>
    run(async () => {
      if (!activeGroupId) throw new Error('Bitte zuerst eine Gruppe anlegen oder auswählen.');

      const bereitsIn = await findItemGroup(hit.id);
      await addItem({
        id: hit.id,
        groupId: activeGroupId,
        kind: hit.kind,
        uri: hit.uri,
        title: hit.title,
        imageUrl: hit.imageUrl,
        trackCount: hit.trackCount,
      });

      await reloadGroups();
      await reloadItems(activeGroupId);
      await refreshCatalog();

      const zielName = groups.find((entry) => entry.id === activeGroupId)?.title ?? 'die Gruppe';
      setMessage(
        bereitsIn && bereitsIn !== activeGroupId
          ? `„${hit.title}" nach „${zielName}" verschoben (war vorher in einer anderen Gruppe).`
          : `„${hit.title}" zu „${zielName}" hinzugefügt.`,
      );
    });

  const handlePhoto = (file: File | undefined) => {
    if (!file) return;
    void run(async () => {
      const dataUrl = await shrinkImage(file, 600);
      const settings = loadSettings();
      try {
        saveSettings({ ...settings, grownUpPhoto: dataUrl });
      } catch {
        throw new Error(
          'Das Foto passt nicht in den Speicher des Browsers. Bitte ein kleineres Bild wählen.',
        );
      }
      setMessage('Eltern-Foto gespeichert.');
    });
  };

  return (
    <div className="admin">
      <header className="admin__bar">
        <button type="button" onClick={() => navigate('/')}>
          Zurück zum Kinder-Bildschirm
        </button>
        <button type="button" onClick={() => navigate('/eltern/diagnose')}>
          Einrichtung &amp; Diagnose
        </button>
      </header>

      {message && (
        <p className="diag__message" role="status">
          {message}
        </p>
      )}

      <section className="card">
        <h2>Abspielgerät</h2>
        <DeviceSettings onMessage={setMessage} />
      </section>

      <section className="card">
        <h2>Gruppen</h2>
        <p className="muted">
          Eine Gruppe ist eine Kachel auf der Startseite, z. B. „Benjamin Blümchen".
        </p>

        <div className="field">
          <label htmlFor="newGroup">Neue Gruppe</label>
          <input
            id="newGroup"
            value={newGroupTitle}
            onChange={(event) => setNewGroupTitle(event.target.value)}
            placeholder="Benjamin Blümchen"
          />
          <button
            type="button"
            disabled={!newGroupTitle.trim() || busy}
            onClick={() =>
              void run(async () => {
                const group = await createGroup(newGroupTitle.trim(), '');
                setNewGroupTitle('');
                await reloadGroups();
                setActiveGroupId(group.id);
              })
            }
          >
            Anlegen
          </button>
        </div>

        {groups.length === 0 ? (
          <p className="muted">Noch keine Gruppe angelegt.</p>
        ) : (
          <ul className="hits">
            {groups.map((group) => (
              <li key={group.id} className="grouprow">
                <div className="grouprow__main">
                  {group.imageUrl ? (
                    <img src={group.imageUrl} alt="" width={56} height={56} />
                  ) : (
                    <span className="grouprow__noimage" aria-hidden="true">
                      ♪
                    </span>
                  )}
                  <span className="hits__text">
                    <input
                      value={group.title}
                      aria-label={`Name der Gruppe ${group.title}`}
                      onChange={(event) => {
                        const title = event.target.value;
                        setGroups((current) =>
                          current.map((entry) =>
                            entry.id === group.id ? { ...entry, title } : entry,
                          ),
                        );
                      }}
                      onBlur={(event) =>
                        void run(async () => {
                          await saveGroup({ ...group, title: event.target.value.trim() });
                          await reloadGroups();
                          await refreshCatalog();
                        })
                      }
                    />
                    <span className="muted">
                      Bild {group.imageManual ? 'selbst gewählt' : 'automatisch'}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setImagePickerFor((current) => (current === group.id ? '' : group.id))
                    }
                  >
                    Bild wählen
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      void run(async () => {
                        await deleteGroup(group.id);
                        await reloadGroups();
                        await refreshCatalog();
                      })
                    }
                  >
                    Löschen
                  </button>
                </div>

                {imagePickerFor === group.id && (
                  <GroupImagePicker
                    groupId={group.id}
                    onChoose={(imageUrl) =>
                      void run(async () => {
                        await setGroupImageManual(group.id, imageUrl);
                        await reloadGroups();
                        setImagePickerFor('');
                        setMessage(`Bild für „${group.title}" gesetzt.`);
                      })
                    }
                    onAuto={() =>
                      void run(async () => {
                        await setGroupImageAuto(group.id);
                        await reloadGroups();
                        setImagePickerFor('');
                        setMessage(`Bild für „${group.title}" wieder automatisch.`);
                      })
                    }
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Folgen suchen und hinzufügen</h2>

        <div className="field">
          <label htmlFor="targetGroup">Hinzufügen zu</label>
          <select
            id="targetGroup"
            value={activeGroupId}
            onChange={(event) => setActiveGroupId(event.target.value)}
          >
            {groups.length === 0 && <option value="">– zuerst eine Gruppe anlegen –</option>}
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.title}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="query">Suche</label>
          <input
            id="query"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void handleSearch(0);
            }}
            placeholder="Benjamin Blümchen Folge 26"
          />
          <button type="button" onClick={() => void handleSearch(0)} disabled={busy}>
            Suchen
          </button>
        </div>

        {hits.length > 0 && (
          <>
            <ul className="hits">
              {hits.map((hit) => (
                <li key={`${hit.kind}-${hit.id}`}>
                  {hit.imageUrl && <img src={hit.imageUrl} alt="" width={56} height={56} />}
                  <span className="hits__text">
                    <strong>{hit.title}</strong>
                    <span className="muted">
                      {hit.subtitle} · {hit.trackCount}{' '}
                      {hit.kind === 'album' ? 'Kapitel' : 'Folgen'}
                    </span>
                  </span>
                  <button type="button" onClick={() => void handleAddHit(hit)} disabled={busy}>
                    Hinzufügen
                  </button>
                </li>
              ))}
            </ul>

            {/* Spotify liefert pro Anfrage hoechstens 10 Treffer je Typ. */}
            <div className="actions">
              <button
                type="button"
                disabled={busy || searchOffset === 0}
                onClick={() => void handleSearch(Math.max(0, searchOffset - SEARCH_PAGE_SIZE))}
              >
                Zurück
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleSearch(searchOffset + SEARCH_PAGE_SIZE)}
              >
                Weitere Treffer
              </button>
            </div>
          </>
        )}
      </section>

      <section className="card">
        <h2>Folgen in „{groups.find((group) => group.id === activeGroupId)?.title ?? '–'}"</h2>
        {items.length === 0 ? (
          <p className="muted">Noch keine Folgen zugeordnet.</p>
        ) : (
          <ul className="hits">
            {items.map((item) => (
              <li key={item.id}>
                {item.imageUrl && <img src={item.imageUrl} alt="" width={56} height={56} />}
                <span className="hits__text">
                  <strong>{item.title}</strong>
                  <span className="muted">{item.trackCount} Kapitel</span>
                </span>

                {groups.length > 1 && (
                  <select
                    value={item.groupId}
                    aria-label={`Gruppe von ${item.title}`}
                    onChange={(event) =>
                      void run(async () => {
                        const targetId = event.target.value;
                        await moveItem(item.id, targetId);
                        await reloadGroups();
                        await reloadItems(activeGroupId);
                        await refreshCatalog();
                        const ziel = groups.find((group) => group.id === targetId);
                        setMessage(`„${item.title}" nach „${ziel?.title ?? '?'}" verschoben.`);
                      })
                    }
                  >
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.title}
                      </option>
                    ))}
                  </select>
                )}

                <button
                  type="button"
                  onClick={() =>
                    void run(async () => {
                      await deleteItem(item.id);
                      await reloadItems(activeGroupId);
                      await refreshCatalog();
                    })
                  }
                >
                  Entfernen
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Eltern-Foto</h2>
        <p className="muted">
          Erscheint, wenn kein Abspielgerät erreichbar ist. Ein echtes Foto versteht ein
          Vierjähriger sofort – anders als jedes Symbol.
        </p>
        <input
          type="file"
          accept="image/*"
          onChange={(event) => handlePhoto(event.target.files?.[0])}
        />
      </section>

      <section className="card">
        <h2>Fortschritt</h2>
        <button
          type="button"
          onClick={() =>
            void run(async () => {
              await resetAllProgress();
              setMessage('Alle Folgen gelten wieder als neu.');
            })
          }
        >
          Allen Hörfortschritt zurücksetzen
        </button>
      </section>
    </div>
  );
}

/**
 * Verkleinert ein Foto vor dem Speichern.
 *
 * localStorage fasst nur wenige Megabyte. Ein unveraendertes Handyfoto
 * sprengt das Limit, und der Fehler faellt sonst erst auf, wenn das Kind vor
 * einem leeren Bildschirm sitzt.
 */
async function shrinkImage(file: File, maxSize: number): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Das Bild konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error('Das Bildformat wird nicht unterstützt.'));
    element.src = dataUrl;
  });

  const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
  const width = Math.round(image.width * scale);
  const height = Math.round(image.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return dataUrl;
  context.drawImage(image, 0, 0, width, height);

  return canvas.toDataURL('image/jpeg', 0.85);
}

/**
 * Zeigt die Cover aller Folgen einer Gruppe zur Auswahl als Gruppenbild.
 * Damit laesst sich ein falsch uebernommenes Bild in einem Tipp korrigieren.
 */
function GroupImagePicker({
  groupId,
  onChoose,
  onAuto,
}: {
  groupId: string;
  onChoose: (imageUrl: string) => void;
  onAuto: () => void;
}) {
  const [choices, setChoices] = useState<CatalogItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    void listItems(groupId).then((result) => {
      if (!cancelled) setChoices(result.filter((item) => item.imageUrl));
    });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  return (
    <div className="imagepicker">
      {choices.length === 0 ? (
        <p className="muted">Diese Gruppe enthält noch keine Folge mit Cover.</p>
      ) : (
        <div className="imagepicker__grid">
          {choices.map((item) => (
            <button
              key={item.id}
              type="button"
              className="imagepicker__choice"
              onClick={() => onChoose(item.imageUrl)}
              title={item.title}
            >
              <img src={item.imageUrl} alt={item.title} width={72} height={72} />
            </button>
          ))}
        </div>
      )}
      <button type="button" onClick={onAuto}>
        Automatisch wählen
      </button>
    </div>
  );
}
